import { useEffect, useState } from "react";
import { CloudArrowUp, Trash, ImageSquare, File, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  deleteMedia,
  humanSize,
  isImage,
  listMedia,
  mediaPublicUrl,
  uploadMedia,
} from "@/lib/cms/media";

/**
 * MediaBrowser — lists uploads in the cms-media bucket for the
 * current org, supports upload + delete, and in `mode="pick"` emits a
 * selected file (with its signed URL) via `onPick(file, url)` for the
 * MarkdownEditor image insert.
 *
 * Also used standalone as the Media library page by mounting it in a
 * container instead of the Sheet (`embed={true}`).
 */
export default function MediaBrowser({
  organizationId,
  onClose,
  onPick,
  mode = "manage", // 'manage' | 'pick'
  embed = false,
}) {
  const [files, setFiles] = useState([]);
  const [urls, setUrls] = useState({}); // fileName -> signedUrl
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listMedia(organizationId);
      setFiles(list);
      // Public bucket → URLs work indefinitely and don't need a refresh.
      const sigs = list
        .filter((f) => isImage(f.name))
        .map((f) => [f.name, mediaPublicUrl(organizationId, f.name)]);
      setUrls(Object.fromEntries(sigs));
    } catch (err) {
      toast.error(err.message || "Could not load media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const onUpload = async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      await uploadMedia({ organizationId, file });
      toast.success("Uploaded");
      await load();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await deleteMedia(organizationId, name);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  const onCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const body = (
    <div data-testid="cms-media-browser" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
          {files.length} file{files.length === 1 ? "" : "s"}
        </p>
        <label
          data-testid="cms-media-upload"
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-hover focus-within:ring-2 focus-within:ring-primary/30"
        >
          <CloudArrowUp size={13} weight="bold" />
          {uploading ? "Uploading…" : "Upload"}
          <input
            data-testid="cms-media-upload-input"
            type="file"
            className="sr-only"
            onChange={onUpload}
            accept="image/*,application/pdf"
            disabled={uploading}
          />
        </label>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : files.length === 0 ? (
        <div
          data-testid="cms-media-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-8 text-center"
        >
          <ImageSquare size={28} weight="duotone" className="mx-auto text-neutral-400" />
          <p className="mt-2 text-sm font-semibold text-neutral-900">No media yet</p>
          <p className="mt-1 text-xs text-neutral-500">
            Upload images (PNG/JPG/WebP) or PDFs to use in pages & announcements.
          </p>
        </div>
      ) : (
        <ul
          data-testid="cms-media-grid"
          className="grid grid-cols-2 gap-3 sm:grid-cols-3"
        >
          {files.map((f) => {
            const img = isImage(f.name);
            const url = urls[f.name];
            return (
              <li
                key={f.name}
                data-testid={`cms-media-item-${f.name}`}
                className="group overflow-hidden rounded-xl border border-neutral-200 bg-white"
              >
                <div className="relative aspect-square bg-neutral-100">
                  {img && url ? (
                    <img src={url} alt="" aria-label={f.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <File size={28} weight="duotone" className="text-neutral-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-start justify-end p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      data-testid={`cms-media-item-${f.name}-delete`}
                      onClick={() => onDelete(f.name)}
                      aria-label="Delete file"
                      className="rounded-full border border-[#C6851E]/40 bg-white/90 p-1.5 text-[#C6851E] shadow-sm backdrop-blur hover:bg-white"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5 px-2 py-2">
                  <p className="truncate text-[11px] font-semibold text-neutral-900" title={f.name}>
                    {f.name}
                  </p>
                  <p className="text-[10px] text-neutral-500">{humanSize(f.metadata?.size)}</p>
                  <div className="flex gap-1">
                    {mode === "pick" && (
                      <button
                        type="button"
                        data-testid={`cms-media-item-${f.name}-pick`}
                        onClick={() => {
                          try {
                            const u = url || mediaPublicUrl(organizationId, f.name);
                            const originalName = f.metadata?.original_name || f.name;
                            onPick({ ...f, display_name: originalName }, u);
                          } catch (err) {
                            toast.error(err.message || "Could not select");
                          }
                        }}
                        className="flex-1 rounded-full bg-primary px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-primary-hover"
                      >
                        Insert
                      </button>
                    )}
                    {mode === "manage" && url && (
                      <button
                        type="button"
                        data-testid={`cms-media-item-${f.name}-copy`}
                        onClick={() => onCopy(url)}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-neutral-200 bg-white px-2 py-1 text-[10px] font-semibold text-neutral-600 hover:bg-neutral-50"
                      >
                        <Copy size={9} weight="bold" />
                        Copy URL
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  if (embed) return body;

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-xl"
        data-testid="cms-media-sheet"
      >
        <SheetHeader>
          <SheetTitle>Media library</SheetTitle>
          <SheetDescription>
            {mode === "pick"
              ? "Pick an image to insert into your content."
              : "Upload, delete, and copy URLs for images and files used in pages & announcements."}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-5">{body}</div>
      </SheetContent>
    </Sheet>
  );
}
